import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EventEmitter, Component, OnInit, ViewChild, Output, Input } from '@angular/core';
import { LazyLoadEvent, Table, SelectItem, FileUpload, ConfirmationService } from 'primeng';
import { IBankInfo } from '../../interfaces/bank';
import { IPageResult } from '../../interfaces/page-result';
import { ISupplierInvoice } from '../../interfaces/supplier-invoice';
import { ISupplierInvoiceFilter } from '../../interfaces/supplier-invoice-filter';
import { ISupplierPayment } from '../../interfaces/supplier-payment';
import { ISupplierPaymentFilter } from '../../interfaces/supplier-payment-filter';
import { BankService } from '../../services';
import { BillingService } from '../../services';
import { FileStorageService } from '../../services';
import { OrganisationsService } from '../../services';
import { ReportService } from '../../services';
import { SelfBillingAgreementService } from '../../services';
import { SupplierInvoiceService } from '../../services';
import { MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import {
  Validators,
  UntypedFormControl,
  UntypedFormGroup,
  FormBuilder,
  NgForm,
} from '@angular/forms';
import { InputHelper } from '../../utils/input.helper';
import { IInvoiceReport } from '../../interfaces/invoice-report';
import { BaseReportComponent } from '../reports/base-report.component';
import moment from 'moment';
import { IImportResult } from '../../interfaces/data-import/import-result';

@Component({
  selector: 'suppliers-invoices-grid',
  templateUrl: './suppliers-invoices-grid.component.html',
})
//@SupplierInvoiceProcessingTypeEnumAware
export class SuppliersInvoicesGridComponent extends BaseReportComponent<any, any, any> {
  @ViewChild('fileuploadInvoice')
  public fileuploadInvoice: FileUpload;

  //----- constants -----
  public processingStatus_batch = 1;
  public processingStatus_failedPayment = 2;
  public processingStatus_cancelled = 3;
  public processingStatus_supplier = 4;
  public processingStatus_unbatchedInvoice = 5;

  public manageMode_supplier = 1;
  public manageMode_vme = 2;

  public paymentStatus_requested = 'requested';
  public paymentStatus_pending = 'pending';
  public paymentStatus_completed = 'completed';
  public paymentStatus_declined = 'declined';
  public paymentStatus_failed = 'failed';

  actionID_cancelInvoice = 1;
  actionID_uncancelInvoice = 2;
  actionID_resendPayment = 3;
  //---------------

  public page: IPageResult<ISupplierInvoice> = {
    items: [],
    total: 0,
  };
  public isLoaded: boolean = false;
  public gridInitiated: boolean = false;
  public filterVal: ISupplierInvoiceFilter;
  public filter: Subject<ISupplierInvoiceFilter>;
  public selectedInvoice = null;

  //--
  public pagePayments: IPageResult<ISupplierPayment> = {
    items: [],
    total: 0,
  };
  public isLoadedPayments: boolean = false;
  public filterValPayments: ISupplierPaymentFilter;
  public filterPayments: Subject<ISupplierPaymentFilter>;
  public paymentsInvoiceId: number;
  //--

  public viewInvoiceId: number;

  public bankAccount: IBankInfo[];
  public supplierAgreedSelfBilling: boolean;

  public invoiceForm: UntypedFormGroup;
  public invoicePdfForm: UntypedFormGroup;
  public selectedInvoicePdf: string;
  public isUploadingPdf: boolean;
  public invoiceReport: UntypedFormGroup;

  //testing
  public isTestMode: boolean;
  public isTest2Mode: boolean;

  public get isSupplierMode(): boolean {
    return this.manageMode == this.manageMode_supplier;
  }

  public get isVmeMode(): boolean {
    return this.manageMode == this.manageMode_vme;
  }

  //------- component attributes -------

  private _invoiceSuppliers: SelectItem[];
  @Input('invoiceSuppliers')
  set invoiceSuppliers(value: SelectItem[]) {
    this._invoiceSuppliers = value;
  }

  get invoiceSuppliers(): SelectItem[] {
    return this._invoiceSuppliers;
  }

  private _manageMode: number;
  @Input('manageMode')
  set manageMode(value: number) {
    this._manageMode = value;
  }

  get manageMode(): number {
    return this._manageMode;
  }

  private _processingStatus: number;
  @Input('processingStatus')
  set processingStatus(value: number) {
    this._processingStatus = value;
  }

  get processingStatus(): number {
    return this._processingStatus;
  }

  private _batchId: number;
  @Input('batchId')
  set batchId(value: number) {
    this._batchId = value;

    if (this.gridInitiated) {
      this.filterVal.filters.batchId = { value: value };
      this.search();
    }
  }

  get batchId(): number {
    return this._batchId;
  }

  private _title: string;
  @Input('title')
  set title(value: string) {
    this._title = value;
  }

  get title(): string {
    return this._title;
  }

  private _showFilterPane: boolean;
  @Input('showFilterPane')
  set showFilterPane(value: boolean) {
    this._showFilterPane = value;
  }

  get showFilterPane(): boolean {
    return this._showFilterPane;
  }

  private _showReportButton: boolean;
  @Input('showReportButton')
  set showReportButton(value: boolean) {
    this._showReportButton = value;
  }

  get showReportButton(): boolean {
    return this._showReportButton;
  }

  @Output() onGridAction: EventEmitter<any>;
  //-------------------------------------------

  cancelInvoiceDialogVisible: boolean = false;
  cancelInvoiceReason: string;

  invoiceReportDialogVisible: boolean = false;

  resendPaymentsDialogVisible: boolean = false;

  constructor(
    reportService: ReportService<any, any>,
    private supplierInvoiceService: SupplierInvoiceService,
    private billingService: BillingService,
    public messageService: MessageService,
    private bankService: BankService,
    private router: Router,
    private route: ActivatedRoute,
    private fileStorageService: FileStorageService,
    private organisationsService: OrganisationsService,
    private selfBillingAgreementService: SelfBillingAgreementService,
    private inputHelper: InputHelper,
    private confirmationService: ConfirmationService,
  ) {
    super(reportService, false);
    this.onGridAction = new EventEmitter<any>();
  }

  ngOnInit() {
    this.isTestMode = this.route.snapshot.params['test'] == '1';
    this.isTest2Mode = this.route.snapshot.params['test'] == '2';

    this.initDefaultFilter();

    if (this.showFilterPane) {
      var datesRange = this.billingService.getLastMonthRange();
      this.filterVal.filters.from = { value: datesRange.from };
      this.filterVal.filters.to = { value: datesRange.to };
    }

    this.filter = new Subject<ISupplierInvoiceFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });
    this.filter.next(this.filterVal);
    this.gridInitiated = true;

    //payments grid
    this.initDefaultFilterPayments();

    this.filterPayments = new Subject<ISupplierPaymentFilter>();
    this.filterPayments.pipe(debounceTime(500)).subscribe((f) => {
      this.loadPayments(f);
      this.filterValPayments = f;
    });
    //this.filterPayments.next(this.filterValPayments);
    //

    if (this.isSupplierMode) {
      this.selfBillingAgreementService.getSelfBillingStatus().then((res) => {
        this.supplierAgreedSelfBilling = res;
      });
    }
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'invoiceNumber',
      sortOrder: 1,
      filters: {
        processingStatus: { value: this._processingStatus },
        batchId: { value: this._batchId },
        from: { value: null },
        to: { value: null },
        supplierId: { value: null },
      },
    };
  }

  initDefaultFilterPayments() {
    this.filterValPayments = {
      first: 0,
      rows: 10,
      sortField: 'CreatedOn',
      sortOrder: 1,
      filters: {},
    };
  }

  load(filter: ISupplierInvoiceFilter) {
    this.supplierInvoiceService.getPage(filter).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;
      //recovering item's selection if its field(s) has been changed
      if (this.selectedInvoice != null) {
        var foundPageItem = this.page.items.find((o) => o.id == this.selectedInvoice.id);

        if (foundPageItem != null) this.selectedInvoice = foundPageItem;
      }
      //
    });
  }

  showInvoiceReport(report: IInvoiceReport) {
    this.invoiceReportDialogVisible = true;
    this.invoiceReport = new UntypedFormGroup({
      numberOfInvoices: new UntypedFormControl(report.numberOfInvoices),
      numberOfTransactions: new UntypedFormControl(report.numberOfTransactions),
      subTotalVAT: new UntypedFormControl(report.subTotalVAT),
      subTotalNoVAT: new UntypedFormControl(report.subTotalNoVAT),
      vat: new UntypedFormControl(report.vat),
      total: new UntypedFormControl(report.total),
    });
  }

  requestInvoiceReport(processing_status) {
    this.filterVal.first = 0;
    // go fetch and show a modal of the results
    this.supplierInvoiceService.getSupplierInvoiceReport(this.filterVal).then((result) => {
      this.showInvoiceReport(result);
    });
  }

  loadPayments(filter: ISupplierPaymentFilter) {
    this.supplierInvoiceService
      .getSupplierInvoicePayments(this.paymentsInvoiceId, filter)
      .then((result) => {
        this.pagePayments.items = result.items;
        this.pagePayments.total = result.total;
        this.page.total = result.total;
        this.isLoadedPayments = true;
      });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filter.next(this.filterVal);
  }

  onGridLazyLoadPayments(event: LazyLoadEvent) {
    this.filterValPayments.first = event.first;
    this.filterValPayments.rows = event.rows;
    this.filterValPayments.sortField = event.sortField;
    this.filterValPayments.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.filterPayments.next(this.filterValPayments);
  }

  clearFilter(dt: Table) {
    dt.sortField = '';
    dt.rows = 10;
    this.initDefaultFilter();
    this.filter.next(this.filterVal);

    this.deselectInvoice();
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.filter.next(this.filterVal);

    this.deselectInvoice();
  }

  //update the items grid with the same filter/paging/sorting settings
  private gridReload() {
    this.load(this.filterVal);
  }

  public gridActionUpdate(actionId: number) {
    if (
      (this.processingStatus == this.processingStatus_batch &&
        actionId == this.actionID_uncancelInvoice) ||
      (this.processingStatus == this.processingStatus_cancelled &&
        actionId == this.actionID_cancelInvoice)
    ) {
      this.gridReload();
    }
  }

  initInvoiceForm(invoice) {
    const vatRateReg = new RegExp(/^(?:100(?:\.00?)?|\d?\d(?:\.\d\d?)?)$/);

    this.invoiceForm = new UntypedFormGroup({
      invoiceNumber: new UntypedFormControl(invoice.invoiceNumber, Validators.required),
      xeroInvoiceNumber: new UntypedFormControl(invoice.xeroInvoiceNumber),
      invoiceDate: new UntypedFormControl(
        new Date(invoice.invoiceDate),
        (validator: UntypedFormControl) => {
          if (!validator.value) {
            return { valid: false, error: 'Is required' };
          }

          return null;
        },
      ),
      supplierName: new UntypedFormControl(invoice.supplierName, Validators.required),
      supplierAddress1: new UntypedFormControl(invoice.supplierAddress1, Validators.required),
      supplierAddress2: new UntypedFormControl(invoice.supplierAddress2),
      supplierAddress3: new UntypedFormControl(invoice.supplierAddress3),
      supplierAddressCity: new UntypedFormControl(invoice.supplierAddressCity, Validators.required),
      supplierAddressPostcode: new UntypedFormControl(
        invoice.supplierAddressPostcode,
        Validators.required,
      ),
      supplierPhone: new UntypedFormControl(invoice.supplierPhone),
      vatNumber: new UntypedFormControl(invoice.vatNumber),
      vatRate: new UntypedFormControl(
        invoice.vatRate,
        Validators.compose([
          Validators.required,
          Validators.min(0.01),
          Validators.max(99.99),
          Validators.pattern(vatRateReg),
        ]),
      ),
      companyRegistrationNumber: new UntypedFormControl(invoice.companyRegistrationNumber),
    });
  }

  initInvoicePdfForm() {
    this.selectedInvoicePdf = null;

    this.invoicePdfForm = new UntypedFormGroup({
      supplierInvoiceNumber: new UntypedFormControl(null, Validators.required),
      supplierInvoiceDate: new UntypedFormControl(null, (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
    });
  }

  onInvoiceSelect(invoice) {
    if (this.viewInvoiceId != invoice.id) this.viewInvoiceId = null;

    this.initInvoiceForm(invoice);

    if (this.isSupplierMode) {
      this.initInvoicePdfForm();
    }

    if (this.isVmeMode) {
      this.isLoadedPayments = false;
      this.paymentsInvoiceId = invoice.id;

      this.initDefaultFilterPayments();

      this.loadPayments(this.filterValPayments);

      this.bankAccount = null;
      this.bankService.getOrgBankAccount(invoice.supplierId).then((res) => {
        this.bankAccount = res;
      });
    }
  }

  public deselectInvoice() {
    this.selectedInvoice = null;
    this.viewInvoiceId = null;
  }

  cancelInvoiceDialog() {
    this.cancelInvoiceReason = null;
    this.cancelInvoiceDialogVisible = true;
  }

  cancelInvoice(invoice, reason) {
    this.supplierInvoiceService
      .cancelInvoice(invoice.id, reason)
      .then(() => {
        this.onGridAction.emit(this.actionID_cancelInvoice);
        this.search();

        this.messageService.add(Message.Success(`Invoice cancelled`, 'Cancel Invoice'));
      })
      .catch(() => {
        this.messageService.add(Message.Error(`Error cancelling invoice`, 'Cancel Invoice'));
      });
  }

  uncancelInvoice(invoice) {
    this.supplierInvoiceService
      .uncancelInvoice(invoice.id)
      .then(() => {
        this.onGridAction.emit(this.actionID_uncancelInvoice);
        this.search();

        this.messageService.add(Message.Success(`Invoice uncancelled`, 'Uncancel Invoice'));
      })
      .catch(() => {
        this.messageService.add(Message.Error(`Error uncancelling invoice`, 'Uncancel Invoice'));
      });
  }

  confirmResendFailedInvoice(invoice) {
    this.billingService.resendFailedInvoice(invoice.id).then(() => {
      this.onGridAction.emit(this.actionID_resendPayment);
      this.search();
    });
  }

  //testing
  removeInvoice(id) {
    this.billingService.removeSupplierInvoice(id).then(() => {
      this.search();
    });
  }

  //testing
  emulateWebhookCompleted(item) {
    this.billingService
      .emulateWebhookEvent({
        event: 'TransactionStateChanged',
        timestamp: new Date(),
        data: {
          id: item.transactionId,
          old_state: 'pending',
          new_state: 'completed',
        },
      })
      .then(() => {
        this.search();
      });
  }

  //testing
  emulateWebhookFailed(item) {
    this.billingService
      .emulateWebhookEvent({
        event: 'TransactionStateChanged',
        timestamp: new Date(),
        data: {
          id: item.transactionId,
          old_state: 'pending',
          new_state: 'failed',
        },
      })
      .then(() => {
        this.search();
      });
  }

  public inputErrorToShow(c: any): string {
    return !c.valid && c.dirty && c._errors && c._errors.error ? c._errors.error : null;
  }

  updateInvoice(id, formValues) {
    var data = formValues;
    data.id = id;
    data.invoiceDate = moment(formValues.invoiceDate).format('YYYY-MM-DD HH:mm');

    this.supplierInvoiceService
      .updateSupplierInvoice(data)
      .then((res) => {
        this.messageService.add(Message.Success('Invoice saved successfully', `Save invoice`));
        this.gridReload();
      })
      .catch(() => {
        this.messageService.add(Message.Error('Error saving invoice', `Save invoice`));
      });
  }

  onSelectInvoicePdf(event: { files: File[] }) {
    this.selectedInvoicePdf = event.files[0].name;

    this.isUploadingPdf = true;
    this.fileuploadInvoice.upload();
  }

  onBeforeUploadInvoicePdf(invoiceId, event: { xhr: XMLHttpRequest; formData: FormData }): void {
    var invoiceData = {
      invoiceId: invoiceId,
      number: this.invoicePdfForm.value.supplierInvoiceNumber,
      date: this.invoicePdfForm.value.supplierInvoiceDate,
    };
    event.formData.append('invoiceData', JSON.stringify(invoiceData));
  }

  pdfSelectorEnabled() {
    return this.invoicePdfForm.valid && !this.isUploadingPdf;
  }

  onUploadInvoicePdf(event) {
    this.isUploadingPdf = false;

    // let response = JSON.parse(event.xhr.response);
    const response = event.originalEvent.body;

    if (response.successfully) {
      this.initInvoicePdfForm();
      this.messageService.add(
        Message.Success('Invoice successfully uploaded', 'Invoice uploading'),
      );
      this.gridReload();
    } else {
      this.onUploadPdfError(response.errorMessage);
    }
  }

  onUploadPdfError(message) {
    this.selectedInvoicePdf = null;
    this.isUploadingPdf = false;

    this.messageService.add(Message.Error(message, 'Invoice uploading'));
  }

  onUploadInvoicePdfError(event) {
    this.onUploadPdfError(
      Message.Error('An error occurred while uploading files. Please try again.', 'File Uploading'),
    );
  }

  openInvoiceViewPanel(id) {
    this.viewInvoiceId = id;
  }

  onInvoiceViewHide() {
    this.viewInvoiceId = null;
  }

  openPdf(objectKey) {
    this.fileStorageService.openSourceInTab(objectKey);
  }

  downloadSupplierReport() {
    this.reportService.url = '/api/report/supplierInvoice';
    this.reportService.downloadReport(this.filterVal);
  }
}
